import { onValue, ref, remove } from 'firebase/database';
import { useEffect, useMemo, useState } from 'react';
import { db } from '../Firebase';

const LEVELS = ['राष्ट्रीय स्तर', 'राज्य स्तर', 'संभाग स्तर', 'जिला स्तर', 'मंदिर स्तर'];

const LEVEL_COLORS = {
  'राष्ट्रीय स्तर': { bg: '#fff0e2', color: '#c45c00', border: '#f5a623' },
  'राज्य स्तर':     { bg: '#e7f1ff', color: '#1255b5', border: '#1675e8' },
  'संभाग स्तर':     { bg: '#e6f8e9', color: '#156b30', border: '#1f9b49' },
  'जिला स्तर':      { bg: '#f1e8ff', color: '#5a1fa8', border: '#8138d7' },
  'मंदिर स्तर':     { bg: '#ffe8ed', color: '#a3001a', border: '#dc2648' },
};

const FOUNDERS = [
  {
    name: "डॉ. अभिषेक कुमार जैन 'शिक्षाचार्य'",
    role: 'निदेशक',
    edu: 'युगल प्रतिष्ठाचार्य, ज्योतिष और वास्तु शास्त्र',
    bio: 'पदस्थ — हिंदी विभाग, एकलव्य विश्वविद्यालय दमोह | श्री शांतिनाथ विंध्यराज समग्र कल्याण संस्था दमोह म.प्र.',
    address: 'एकलव्य विश्वविद्यालय, दमोह, मध्य प्रदेश',
    img: '/abhishek.jpeg',
  },
  {
    name: "डॉ. आशीष कुमार जैन 'शिक्षाचर्या'",
    role: 'निदेशक',
    edu: 'युगल प्रतिष्ठाचार्य, ज्योतिष और वास्तु शास्त्र',
    bio: 'पदस्थ — संस्कृत विभाग, एकलव्य विश्वविद्यालय दमोह म.प्र. | संस्कृत-प्राकृत तथा प्राच्यविद्या अनुसंधान केंद्र दमोह म.प्र.',
    address: 'एकलव्य विश्वविद्यालय, दमोह, मध्य प्रदेश',
    img: '/Dr.ashish.jpeg',
  },
  {
    name: 'श्री संदीप जैन',
    role: 'संचालक',
    edu: 'जैनश्री कंप्यूटर दमोह',
    bio: 'डायरेक्टर — अक्षर डिजिटल टेक्नोलॉजी कंपनी, दमोह म.प्र. | डायरेक्टर — अक्षर फाउंडेशन, दमोह म.प्र.',
    address: 'दमोह, मध्य प्रदेश',
    img: '/Sandeep Jain.jpeg',
  },
];

const VALUES = [
  { icon: '🤝', title: 'एकता',    desc: 'क्षेत्रों में जैन परिवारों को एकजुट करना' },
  { icon: '🔒', title: 'विश्वास', desc: 'सुरक्षित और विश्वसनीय डेटा प्रबंधन' },
  { icon: '💡', title: 'नवाचार', desc: 'समुदाय की जरूरतों के लिए आधुनिक तकनीक' },
  { icon: '🌱', title: 'विरासत', desc: 'जैन संस्कृति और परंपराओं का संरक्षण' },
];

/* ── Initials avatar fallback ── */
const Initials = ({ name, size = 80 }) => {
  const letters = (name || '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg,#213F6D,#193264)',
      color: '#fff', display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontWeight: 800,
      fontSize: size * 0.32, flexShrink: 0,
    }}>
      {letters || 'जै'}
    </div>
  );
};

/* ── Single committee card ── */
const MemberCard = ({ m, isAdmin, onDelete, openImage }) => {
  const lc = LEVEL_COLORS[m.level] || LEVEL_COLORS['मंदिर स्तर'];
  const location = [m.state, m.division, m.district, m.temple].filter(Boolean).join(' • ') || 'राष्ट्रीय कार्यालय';
  const [imgErr, setImgErr] = useState(false);

  return (
    <div style={{
      background: '#fff', borderRadius: 16, overflow: 'hidden',
      position: 'relative',
      boxShadow: '0 4px 20px rgba(33,63,109,0.10)',
      border: `1.5px solid ${lc.border}22`,
      transition: 'transform .25s, box-shadow .25s',
      display: 'flex', flexDirection: 'column',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = `0 12px 32px ${lc.border}33`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(33,63,109,0.10)'; }}
    >
      {/* Photo strip */}
      <div style={{ background: `linear-gradient(135deg,${lc.border}22,${lc.border}44)`, padding: '24px 0 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        {m.photo && !imgErr
          ? <img src={m.photo} alt={m.name} onError={() => setImgErr(true)}
              style={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${lc.border}`, boxShadow: `0 4px 14px ${lc.border}55`, cursor: openImage ? 'pointer' : 'default' }}
              onClick={() => openImage && openImage(m.photo)} />
          : <Initials name={m.name} size={88} />
        }
        <span style={{ background: lc.bg, color: lc.color, border: `1px solid ${lc.border}`, borderRadius: 50, padding: '3px 14px', fontSize: 11, fontWeight: 700 }}>
          {m.level}
        </span>
      </div>

      {isAdmin && (
        <button onClick={() => onDelete && onDelete(m)} title="हटाएं" style={{ position: 'absolute', top: 10, right: 10, border: 'none', background: 'rgba(255,255,255,0.9)', padding: 6, borderRadius: 8, cursor: 'pointer' }}>🗑️</button>
      )}

      {/* Info */}
      <div style={{ padding: '14px 18px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h5 style={{ margin: 0, color: '#1E293B', fontSize: '1rem', fontWeight: 700, lineHeight: 1.3 }}>{m.name}</h5>
        <div style={{ display: 'inline-block', background: 'linear-gradient(135deg,#213F6D,#193264)', color: '#fff', borderRadius: 50, padding: '2px 12px', fontSize: 11, fontWeight: 600, alignSelf: 'flex-start' }}>
          {m.post}
        </div>
        {m.phone && (
          <a href={`tel:${m.phone}`} style={{ color: '#213F6D', fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
            📞 {m.phone}
          </a>
        )}
        {m.email && (
          <a href={`mailto:${m.email}`} style={{ color: '#555', fontSize: 12, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5, wordBreak: 'break-all' }}>
            ✉️ {m.email}
          </a>
        )}
        <div style={{ marginTop: 'auto', paddingTop: 10, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <span style={{ fontSize: 13, flexShrink: 0 }}>📍</span>
          <span style={{ color: '#64748B', fontSize: 12, lineHeight: 1.5 }}>{location}</span>
        </div>
      </div>
    </div>
  );
};

/* ── Main About component ── */
const About = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLevel, setActiveLevel] = useState('');
  const [activeState, setActiveState] = useState('');
  const [activeDivision, setActiveDivision] = useState('');
  const [activeDistrict, setActiveDistrict] = useState('');
  const [activePost, setActivePost] = useState('');
  const [search, setSearch] = useState('');
  const [imageViewer, setImageViewer] = useState(null);
  const [activeTab] = useState('team');
  const isAdmin = JSON.parse(localStorage.getItem('loggedUser') || 'null')?.role === 'Admin';

  useEffect(() => {
    const unsub = onValue(ref(db, 'team_members'), (snap) => {
      if (snap.exists()) {
        setMembers(Object.entries(snap.val()).map(([id, v]) => ({ id, ...v })));
      } else {
        setMembers([]);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return members.filter((m) =>
      (!activeLevel || m.level === activeLevel) &&
      (!activeState || m.state === activeState) &&
      (!activeDivision || m.division === activeDivision) &&
      (!activeDistrict || m.district === activeDistrict) &&
      (!activePost || m.post === activePost) &&
      (!q || [m.name, m.post, m.phone, m.state, m.district, m.division, m.temple].join(' ').toLowerCase().includes(q))
    );
  }, [members, activeLevel, search, activeState, activeDivision, activeDistrict, activePost]);

  const countByLevel = useMemo(() => {
    const map = {};
    LEVELS.forEach((l) => { map[l] = members.filter((m) => m.level === l).length; });
    return map;
  }, [members]);

  const STATES = useMemo(() => {
    return [...new Set(members.map((m) => m.state).filter(Boolean))].sort();
  }, [members]);

  const DIVISIONS = useMemo(() => {
    return [...new Set(members.filter(m => !activeState || m.state === activeState).map(m => m.division).filter(Boolean))].sort();
  }, [members, activeState]);

  const DISTRICTS = useMemo(() => {
    return [...new Set(members.filter(m => (!activeDivision ? true : m.division === activeDivision)).map(m => m.district).filter(Boolean))].sort();
  }, [members, activeDivision]);

  const POSTS = useMemo(() => {
    return [...new Set(members.map((m) => m.post).filter(Boolean))].sort();
  }, [members]);

  const handleDelete = async (m) => {
    if (!isAdmin) return;
    if (!window.confirm(`क्या आप "${m.name}" को हटाना चाहते हैं?`)) return;
    try {
      await remove(ref(db, `team_members/${m.id}`));
    } catch (e) {
      alert('त्रुटि: ' + e.message);
    }
  };
  
  return (
    <div style={{ background: '#f7f8fb', minHeight: '100vh' }}>

      {/* ── Hero (kept minimal - focus on हमारी टीम) ── */}
      <div style={{
        background: 'linear-gradient(135deg,#213F6D 0%,#193264 100%)',
        padding: 'clamp(48px,8vw,88px) 20px clamp(32px,6vw,64px)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

        <h2 style={{ color: '#fff', fontSize: 'clamp(1.5rem,4vw,2.2rem)', fontWeight: 800, marginBottom: 12 }}>हमारी टीम</h2>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(0.95rem,2vw,1.05rem)', maxWidth: 720, margin: '0 auto 12px' }}>
          राष्ट्रीय से मंदिर स्तर तक — हमारी समर्पित टीम
        </p>
      </div>

      {/* ══════════════ TEAM ══════════════ */}
      {activeTab === 'team' && (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(24px,4vw,48px) clamp(16px,4vw,24px)' }}>

          {/* Section header */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <span style={{ display: 'inline-block', background: '#213F6D', color: '#fff', padding: '5px 20px', borderRadius: 50, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
              👥 समिति सदस्य
            </span>
            <h2 style={{ color: '#213F6D', fontSize: 'clamp(1.4rem,3.5vw,2.2rem)', fontWeight: 800, marginBottom: 8 }}>हमारी टीम</h2>
            <p style={{ color: '#64748B', fontSize: '1rem', maxWidth: 560, margin: '0 auto' }}>
              राष्ट्रीय से मंदिर स्तर तक — जैन समग्र पोर्टल की समर्पित टीम
            </p>
          </div>

          {/* Level stats strip */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginBottom: 28 }}>
            {LEVELS.map((l) => {
              const lc = LEVEL_COLORS[l];
              return (
                <div key={l} style={{ background: lc.bg, border: `1.5px solid ${lc.border}`, borderRadius: 12, padding: '10px 18px', textAlign: 'center', minWidth: 110 }}>
                  <div style={{ color: lc.color, fontWeight: 800, fontSize: '1.4rem', lineHeight: 1 }}>{countByLevel[l]}</div>
                  <div style={{ color: lc.color, fontSize: 11, fontWeight: 600, marginTop: 3 }}>{l}</div>
                </div>
              );
            })}
            <div style={{ background: '#f0f4ff', border: '1.5px solid #213F6D', borderRadius: 12, padding: '10px 18px', textAlign: 'center', minWidth: 110 }}>
              <div style={{ color: '#213F6D', fontWeight: 800, fontSize: '1.4rem', lineHeight: 1 }}>{members.length}</div>
              <div style={{ color: '#213F6D', fontSize: 11, fontWeight: 600, marginTop: 3 }}>कुल सदस्य</div>
            </div>
          </div>

          {/* Search + filter */}
          <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', marginBottom: 28, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            <input
              placeholder="🔍 नाम, पद, मोबाइल, स्थान से खोजें..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: '10px 14px', border: '1.5px solid #d5d8df', borderRadius: 10, fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
            />
            <select value={activeState} onChange={e => { setActiveState(e.target.value); setActiveDivision(''); setActiveDistrict(''); }} style={{ padding: '8px 12px', border: '1.5px solid #d5d8df', borderRadius: 10, minWidth: 160, background: '#fff', fontSize: 13 }}>
              <option value="">राज्य चुनें</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={activeDivision} onChange={e => { setActiveDivision(e.target.value); setActiveDistrict(''); }} disabled={!activeState} style={{ padding: '8px 12px', border: '1.5px solid #d5d8df', borderRadius: 10, minWidth: 160, background: '#fff', fontSize: 13 }}>
              <option value="">संभाग चुनें</option>
              {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={activeDistrict} onChange={e => setActiveDistrict(e.target.value)} disabled={!activeDivision} style={{ padding: '8px 12px', border: '1.5px solid #d5d8df', borderRadius: 10, minWidth: 160, background: '#fff', fontSize: 13 }}>
              <option value="">जिला चुनें</option>
              {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={activePost} onChange={e => setActivePost(e.target.value)} style={{ padding: '8px 12px', border: '1.5px solid #d5d8df', borderRadius: 10, minWidth: 140, background: '#fff', fontSize: 13 }}>
              <option value="">पद</option>
              {POSTS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button onClick={() => setActiveLevel('')} style={{ padding: '8px 16px', borderRadius: 50, border: '1.5px solid #213F6D', background: activeLevel === '' ? '#213F6D' : '#fff', color: activeLevel === '' ? '#fff' : '#213F6D', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all .2s' }}>
                सभी ({members.length})
              </button>
              {LEVELS.map((l) => {
                const lc = LEVEL_COLORS[l];
                const active = activeLevel === l;
                return (
                  <button key={l} onClick={() => setActiveLevel(active ? '' : l)} style={{ padding: '8px 16px', borderRadius: 50, border: `1.5px solid ${lc.border}`, background: active ? lc.border : lc.bg, color: active ? '#fff' : lc.color, fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all .2s' }}>
                    {l} ({countByLevel[l]})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Cards grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div className="spinner-border" style={{ color: '#213F6D', width: 48, height: 48 }} />
              <p style={{ color: '#64748B', marginTop: 16 }}>टीम सदस्य लोड हो रहे हैं...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748B' }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>👥</div>
              <h5>कोई सदस्य नहीं मिला।</h5>
              <p style={{ fontSize: 14 }}>फ़िल्टर बदलें या खोज शब्द हटाएं।</p>
            </div>
          ) : (
            <>
              <p style={{ color: '#64748B', fontSize: 13, marginBottom: 16 }}>
                {filtered.length} सदस्य मिले{activeLevel ? ` — ${activeLevel}` : ''}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 20 }}>
                  {filtered.map((m) => <MemberCard key={m.id} m={m} isAdmin={isAdmin} onDelete={handleDelete} openImage={(src)=>setImageViewer(src)} />)}
              </div>
            </>
          )}

        </div>
      )}

      {imageViewer && (
        <div onClick={() => setImageViewer(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:3000}}>
          <img src={imageViewer} alt="preview" style={{maxWidth:'95%',maxHeight:'95%',borderRadius:8,boxShadow:'0 8px 40px rgba(0,0,0,0.6)'}} />
        </div>
      )}

    </div>
  );
};

export default About;
